if (!customElements.get('licensed-judgeme-reviews')) {
  customElements.define('licensed-judgeme-reviews', class extends HTMLElement {
    connectedCallback() {
      this.shop = this.dataset.shopDomain || '';
      this.token = (this.dataset.publicToken || '').trim();
      this.pageSize = Math.max(1, Number(this.dataset.reviewsPerPage || 6));
      this.verifiedLabel = this.dataset.verifiedLabel || 'Verified buyer';
      this.loadLabel = this.dataset.loadMoreLabel || 'Load More';
      this.fewerLabel = this.dataset.showFewerLabel || 'Show Fewer Reviews';
      this.emptyText = this.dataset.emptyText || 'Reviews for this licensed collection are not available right now.';
      this.errorText = this.dataset.errorText || 'Reviews could not be loaded. Please try again.';
      this.grid = this.querySelector('[data-review-grid]');
      this.status = this.querySelector('[data-review-status]');
      this.summary = this.querySelector('[data-review-summary]');
      this.averageNode = this.querySelector('[data-average-rating]');
      this.countNode = this.querySelector('[data-review-count]');
      this.moreButton = this.querySelector('[data-load-more]');
      this.fewerButton = this.querySelector('[data-show-fewer]');
      this.native = this.querySelector('[data-native-source]');
      this.products = this.readProducts();
      this.reviews = [];
      this.visibleCount = this.pageSize;
      this.destroyed = false;

      this.moreButton?.addEventListener('click', () => this.loadMore());
      this.fewerButton?.addEventListener('click', () => this.showFewer());
      this.init();
    }

    disconnectedCallback() {
      this.destroyed = true;
    }

    readProducts() {
      const node = this.querySelector('[data-licensed-products]');
      if (!node) return [];
      try {
        const value = JSON.parse(node.textContent || '[]');
        return Array.isArray(value)
          ? value.filter((item) => item?.id && item?.handle)
          : [];
      } catch (error) {
        return [];
      }
    }

    apiUrl(product, page) {
      const url = new URL('https://judge.me/api/v1/widgets/product_review');
      url.search = new URLSearchParams({
        shop_domain: this.shop,
        api_token: this.token,
        external_id: String(product.id),
        handle: product.handle,
        page: String(page),
        per_page: '100',
        json_request: 'true'
      });
      return url.toString();
    }

    async init() {
      this.showStatus(this.dataset.loadingText || 'Loading customer reviews…');
      this.grid?.setAttribute('aria-busy', 'true');

      try {
        if (this.token && this.products.length) {
          this.reviews = await this.fetchCollectionReviews();
        } else {
          this.reviews = await this.readNativeReviews();
        }

        this.reviews = this.unique(this.reviews).sort((a, b) => this.time(b.date) - this.time(a.date));
        this.visibleCount = Math.min(this.pageSize, this.reviews.length);
        this.render();
      } catch (error) {
        console.warn('[Licensed Judge.me reviews]', error);
        try {
          this.reviews = this.unique(await this.readNativeReviews());
          this.visibleCount = Math.min(this.pageSize, this.reviews.length);
          this.render();
        } catch (fallbackError) {
          console.warn('[Licensed Judge.me fallback]', fallbackError);
          this.reviews = [];
          this.render();
          this.showStatus(this.errorText);
        }
      }
    }

    async fetchCollectionReviews() {
      const results = [];
      const queue = [...this.products];
      const workerCount = Math.min(4, queue.length);

      const worker = async () => {
        while (queue.length && !this.destroyed) {
          const product = queue.shift();
          const productReviews = await this.fetchProductReviews(product);
          results.push(...productReviews);
        }
      };

      await Promise.all(Array.from({ length: workerCount }, worker));
      return results;
    }

    async fetchProductReviews(product) {
      const reviews = [];
      let page = 1;
      let keepGoing = true;
      let guard = 0;

      while (keepGoing && guard < 20 && !this.destroyed) {
        guard += 1;
        const result = await this.fetchPage(this.apiUrl(product, page), product.title);
        reviews.push(...result.reviews);
        keepGoing = result.hasMore;
        page += 1;
      }

      return reviews;
    }

    async fetchPage(url, fallbackProductTitle) {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'omit'
      });
      if (!response.ok) throw new Error(`Judge.me ${response.status}`);
      const text = await response.text();
      return this.parse(text, fallbackProductTitle);
    }

    parse(text, fallbackProductTitle) {
      let payload;
      try {
        payload = JSON.parse(text);
      } catch (error) {
        return this.parseHtml(text, fallbackProductTitle);
      }

      const rawReviews = [
        payload?.reviews,
        payload?.data?.reviews,
        payload?.review_widget?.reviews,
        payload?.widget?.reviews
      ].find(Array.isArray);

      if (!rawReviews) {
        const html = payload.widget || payload.html || payload.data?.widget || payload.data?.html || payload.review_widget || '';
        return this.parseHtml(html, fallbackProductTitle);
      }

      const meta = payload.meta || payload.pagination || payload.data?.meta || {};
      const currentPage = Number(meta.current_page || meta.page || payload.page || 1);
      const totalPages = Number(meta.total_pages || meta.pages || payload.total_pages || 0);
      const hasMore = totalPages ? currentPage < totalPages : rawReviews.length >= 100;

      return {
        reviews: rawReviews
          .map((review, index) => this.normalize(review, index, fallbackProductTitle))
          .filter((review) => review.body || review.title),
        hasMore
      };
    }

    normalize(review, index, fallbackProductTitle) {
      const reviewer = review.reviewer || {};
      return {
        id: String(review.uuid || review.id || review.review_uuid || `${review.reviewer_name || reviewer.name || 'review'}-${review.created_at || index}`),
        rating: this.clampRating(review.rating || review.score || review.stars),
        title: this.clean(review.title || review.review_title),
        body: this.clean(review.body || review.content || review.review_body || review.text),
        author: this.clean(review.reviewer_name || review.name || reviewer.name || 'Anonymous'),
        verified: Boolean(review.verified || review.verified_buyer || review.is_verified || review.buyer_verified || reviewer.verified),
        date: review.created_at || review.reviewed_at || review.date || '',
        product: this.clean(review.product_title || review.product?.title || fallbackProductTitle)
      };
    }

    parseHtml(html, fallbackProductTitle = '') {
      const root = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
      const reviews = [...root.querySelectorAll('.jdgm-rev')].map((element, index) => {
        const rating = element.querySelector('[data-score], .jdgm-rev__rating');
        return {
          id: String(element.dataset.reviewId || element.dataset.id || element.id || `native-${index}-${element.textContent.length}`),
          rating: this.clampRating(rating?.dataset.score || rating?.getAttribute('data-rating')),
          title: this.text(element, '.jdgm-rev__title'),
          body: this.text(element, '.jdgm-rev__body'),
          author: this.text(element, '.jdgm-rev__author') || 'Anonymous',
          verified: Boolean(element.querySelector('.jdgm-rev__buyer-badge, [class*=verified]')),
          date: this.text(element, '.jdgm-rev__timestamp'),
          product: this.text(element, '.jdgm-rev__prod-link') || fallbackProductTitle
        };
      }).filter((review) => review.body || review.title);

      const nextElement = root.querySelector('.jdgm-paginate__next-page:not(.jdgm-disabled), [rel=next]');
      return { reviews, hasMore: Boolean(nextElement) };
    }

    async readNativeReviews() {
      if (!this.native) return [];
      const read = () => this.parseHtml(this.native).reviews;
      const immediate = read();
      if (immediate.length) return immediate;

      return new Promise((resolve) => {
        const observer = new MutationObserver(() => {
          const reviews = read();
          if (!reviews.length) return;
          observer.disconnect();
          resolve(reviews);
        });
        observer.observe(this.native, { childList: true, subtree: true, attributes: true });
        window.setTimeout(() => {
          observer.disconnect();
          resolve(read());
        }, 3000);
      });
    }

    render() {
      if (!this.grid) return;
      const visible = this.reviews.slice(0, this.visibleCount);
      const fragment = document.createDocumentFragment();
      visible.forEach((review) => fragment.append(this.card(review)));
      this.grid.replaceChildren(fragment);
      this.grid.hidden = !this.reviews.length;
      this.grid.setAttribute('aria-busy', 'false');
      this.showStatus(this.reviews.length ? '' : this.emptyText);
      this.updateSummary();
      this.updateButtons();
    }

    updateSummary() {
      if (!this.reviews.length) {
        if (this.summary) this.summary.hidden = true;
        return;
      }

      const average = this.reviews.reduce((sum, review) => sum + review.rating, 0) / this.reviews.length;
      if (this.averageNode) this.averageNode.textContent = average.toFixed(1);
      if (this.countNode) this.countNode.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-GB').format(this.reviews.length);
      if (this.summary) this.summary.hidden = false;
    }

    updateButtons() {
      if (this.moreButton) {
        this.moreButton.hidden = this.visibleCount >= this.reviews.length;
        this.moreButton.textContent = this.loadLabel;
      }
      if (this.fewerButton) {
        this.fewerButton.hidden = this.visibleCount <= this.pageSize;
        this.fewerButton.textContent = this.fewerLabel;
      }
    }

    loadMore() {
      this.visibleCount = Math.min(this.visibleCount + this.pageSize, this.reviews.length);
      this.render();
    }

    showFewer() {
      this.visibleCount = Math.min(this.pageSize, this.reviews.length);
      this.render();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    card(review) {
      const article = document.createElement('article');
      article.className = 'pdp-judgeme-reviews__card';

      const header = document.createElement('div');
      header.className = 'pdp-judgeme-reviews__card-header';
      const stars = document.createElement('span');
      stars.className = 'pdp-judgeme-reviews__card-stars';
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      header.append(stars);

      if (review.verified) {
        const badge = document.createElement('span');
        badge.className = 'pdp-judgeme-reviews__verified';
        badge.textContent = this.verifiedLabel;
        header.append(badge);
      }
      article.append(header);

      if (review.title) {
        const title = document.createElement('h3');
        title.className = 'pdp-judgeme-reviews__card-title';
        title.textContent = review.title;
        article.append(title);
      }

      if (review.body) {
        const body = document.createElement('p');
        body.className = 'pdp-judgeme-reviews__card-body';
        body.textContent = `“${review.body}”`;
        article.append(body);
      }

      const footer = document.createElement('footer');
      footer.className = 'pdp-judgeme-reviews__card-footer';
      const reviewer = document.createElement('div');
      reviewer.className = 'pdp-judgeme-reviews__reviewer';
      const name = document.createElement('strong');
      name.textContent = review.author;
      reviewer.append(name);

      if (review.product) {
        const product = document.createElement('span');
        product.textContent = review.product;
        reviewer.append(product);
      }
      footer.append(reviewer);

      if (review.date) {
        const date = document.createElement('span');
        date.className = 'pdp-judgeme-reviews__date';
        date.textContent = this.formatDate(review.date);
        footer.append(date);
      }

      article.append(footer);
      return article;
    }

    formatDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return this.clean(value);
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).format(date);
    }

    time(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    }

    unique(reviews) {
      const seen = new Set();
      return reviews.filter((review) => {
        const key = this.clean(`${review.id}|${review.author}|${review.body || review.title}`).toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    showStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
    }

    text(root, selector) {
      return this.clean(root.querySelector(selector)?.textContent);
    }

    clean(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    clampRating(value) {
      return Math.max(1, Math.min(5, Math.round(Number(value) || 5)));
    }
  });
}
